import React, { useEffect, useState } from "react";
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar,
} from "recharts";
import {
    Flame, BookCheck, Clock, Gauge, Zap, BookOpen, Brain, TrendingUp, Loader2, Trophy, MoveHorizontal, AlignLeft,
} from "lucide-react";
import { analyticsApi } from "@/lib/apiClient";
import { useTranslation } from "@/context/LanguageContext";
import { motion } from "framer-motion";

const StatCard = ({ icon: Icon, label, value, suffix, accent, index, testid }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
        className="rounded-2xl border border-border bg-surface-1 p-5 transition-colors hover:bg-surface-2"
        data-testid={testid}
    >
        <div className="flex items-center justify-between">
            <span className="mono-label text-t4">{label}</span>
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-surface-3" style={{ color: accent }}>
                <Icon className="h-4 w-4" />
            </div>
        </div>
        <div className="mt-3 font-sans-display text-2xl font-semibold text-t1">
            {value}{suffix && <span className="ml-1 text-sm font-normal text-t4">{suffix}</span>}
        </div>
    </motion.div>
);

const TEAL = "hsl(174, 72%, 46%)";
const BLUE = "hsl(206, 70%, 52%)";

const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="rounded-lg border border-border bg-surface-2 px-3 py-2 text-xs shadow-elev-1">
            <div className="mb-1 font-mono-ui text-t4">{label}</div>
            {payload.map((p, i) => (
                <div key={i} className="text-t1">{p.name}: <span style={{ color: p.color }}>{p.value}</span></div>
            ))}
        </div>
    );
};

const heatColor = (words, max) => {
    if (!words) return "hsl(222 16% 13%)";
    const t = Math.min(1, words / (max || 1));
    const light = 18 + t * 34;
    return `hsl(174 60% ${light}%)`;
};

const READING_MODE_META = {
    rsvp: { icon: Zap, color: "hsl(174, 72%, 46%)" },
    ticker: { icon: MoveHorizontal, color: "hsl(206, 70%, 52%)" },
    focus: { icon: AlignLeft, color: "hsl(38, 92%, 60%)" },
};

const ReadingStyleCard = ({ modes = [], preferred, t }) => {
    const ordered = ["rsvp", "ticker", "focus"].map((k) => modes.find((m) => m.mode === k) || { mode: k, minutes: 0, words: 0, sessions: 0 });
    const maxMin = Math.max(1, ...ordered.map((m) => m.minutes || 0));
    const hasData = ordered.some((m) => (m.minutes || 0) > 0 || (m.sessions || 0) > 0);
    const prefMeta = preferred ? READING_MODE_META[preferred] : null;
    const prefLabel = preferred ? t(`reader.modes.${preferred}.label`) : "";
    return (
        <div className="rounded-2xl border border-border bg-surface-1 p-5" data-testid="analytics-reading-style">
            <div className="mb-1 flex items-center justify-between">
                <h3 className="font-sans-display text-sm font-semibold text-t1">{t("analytics.readingStyle")}</h3>
                {prefMeta && (
                    <span data-testid="analytics-preferred-mode" className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary))]/15 px-2.5 py-0.5 text-[11px] font-medium text-[hsl(var(--primary))]">
                        <prefMeta.icon className="h-3 w-3" /> {t("analytics.prefers", { mode: prefLabel })}
                    </span>
                )}
            </div>
            <p className="mb-4 text-xs text-t4">{t("analytics.readingStyleDesc")}</p>
            {!hasData ? (
                <p className="py-6 text-center text-sm text-t3">{t("analytics.readingStyleEmpty")}</p>
            ) : (
                <div className="space-y-3.5">
                    {ordered.map((m) => {
                        const meta = READING_MODE_META[m.mode];
                        const Icon = meta.icon;
                        const pct = Math.round(((m.minutes || 0) / maxMin) * 100);
                        const isPref = preferred === m.mode;
                        return (
                            <div key={m.mode} data-testid={`reading-style-row-${m.mode}`}>
                                <div className="mb-1 flex items-center justify-between text-sm">
                                    <span className="flex items-center gap-2 text-t2">
                                        <Icon className="h-4 w-4" style={{ color: meta.color }} />
                                        {t(`reader.modes.${m.mode}.label`)}
                                        {isPref && <span className="mono-label text-[9px] text-[hsl(var(--primary))]">TOP</span>}
                                    </span>
                                    <span className="font-mono-ui text-xs text-t3">{m.minutes} {t("analytics.minShort")} · {m.sessions} {t("analytics.sessions")}</span>
                                </div>
                                <div className="h-2 w-full overflow-hidden rounded-full bg-surface-3">
                                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: meta.color }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const BADGES = [
    { key: "first", icon: BookOpen, test: (s) => s.library_count >= 1 },
    { key: "streak3", icon: Flame, test: (s) => s.reading_streak >= 3 },
    { key: "fast", icon: Zap, test: (s) => s.fastest_wpm >= 500 },
    { key: "finisher", icon: BookCheck, test: (s) => s.books_completed >= 1 },
    { key: "scholar", icon: Brain, test: (s) => s.total_words_read >= 5000 },
];

export default function Analytics() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try { setData(await analyticsApi.overview()); }
            finally { setLoading(false); }
        })();
    }, []);

    if (loading) {
        return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>;
    }
    const s = data?.stats || {};
    const maxHeat = Math.max(1, ...(data?.heatmap || []).map((d) => d.words));

    return (
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:py-10">
            <div>
                <h1 className="font-sans-display text-2xl font-semibold tracking-tight text-t1 sm:text-3xl">{t("analytics.title")}</h1>
                <p className="mt-1.5 text-t3">{t("analytics.subtitle")}</p>
            </div>

            {/* Stats */}
            <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
                <StatCard testid="stat-streak" index={0} icon={Flame} label={t("analytics.streak")} value={s.reading_streak || 0} suffix={t("analytics.daysSuffix")} accent="hsl(38 92% 62%)" />
                <StatCard testid="stat-hours" index={1} icon={Clock} label={t("analytics.hoursRead")} value={s.hours_read || 0} suffix={t("analytics.hoursSuffix")} accent={BLUE} />
                <StatCard testid="stat-avg-wpm" index={2} icon={Gauge} label={t("analytics.avgWpm")} value={s.avg_wpm || 0} accent={TEAL} />
                <StatCard testid="stat-fastest-wpm" index={3} icon={Zap} label={t("analytics.fastestWpm")} value={s.fastest_wpm || 0} accent="hsl(174 72% 46%)" />
                <StatCard testid="stat-library" index={4} icon={BookOpen} label={t("analytics.inLibrary")} value={s.library_count || 0} accent={BLUE} />
                <StatCard testid="stat-completed" index={5} icon={BookCheck} label={t("analytics.completed")} value={s.books_completed || 0} accent={TEAL} />
                <StatCard testid="stat-words" index={6} icon={TrendingUp} label={t("analytics.wordsRead")} value={(s.total_words_read || 0).toLocaleString()} accent={BLUE} />
                <StatCard testid="stat-retention" index={7} icon={Brain} label={t("analytics.retention")} value={s.retention_score || 0} suffix="%" accent="hsl(152 52% 46%)" />
            </div>

            {/* Charts */}
            <div className="mt-6 grid gap-4 lg:grid-cols-2">
                <div className="rounded-2xl border border-border bg-surface-1 p-5" data-testid="analytics-wpm-chart">
                    <h3 className="mb-4 font-sans-display text-sm font-semibold text-t1">{t("analytics.wpmTrend")}</h3>
                    <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={data?.wpm_trend || []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 14% 18%)" vertical={false} />
                            <XAxis dataKey="label" tick={{ fill: "hsl(215 10% 58%)", fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: "hsl(215 10% 58%)", fontSize: 11 }} axisLine={false} tickLine={false} width={28} />
                            <Tooltip content={<ChartTooltip />} />
                            <Line type="monotone" dataKey="wpm" name="WPM" stroke={TEAL} strokeWidth={2.5} dot={{ r: 2.5, fill: TEAL }} activeDot={{ r: 4 }} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>

                <div className="rounded-2xl border border-border bg-surface-1 p-5" data-testid="analytics-minutes-chart">
                    <h3 className="mb-4 font-sans-display text-sm font-semibold text-t1">{t("analytics.minutesTrend")}</h3>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={data?.wpm_trend || []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 14% 18%)" vertical={false} />
                            <XAxis dataKey="label" tick={{ fill: "hsl(215 10% 58%)", fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: "hsl(215 10% 58%)", fontSize: 11 }} axisLine={false} tickLine={false} width={28} />
                            <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(222 16% 13%)" }} />
                            <Bar dataKey="minutes" name={t("analytics.minShort")} fill={BLUE} radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Reading style preference */}
            <div className="mt-6">
                <ReadingStyleCard modes={data?.reading_modes || []} preferred={data?.preferred_mode} t={t} />
            </div>

            {/* Heatmap + badges */}
            <div className="mt-6 grid gap-4 lg:grid-cols-[1fr_320px]">
                <div className="rounded-2xl border border-border bg-surface-1 p-5" data-testid="analytics-heatmap">
                    <h3 className="mb-4 font-sans-display text-sm font-semibold text-t1">{t("analytics.activity")}</h3>
                    <div className="grid grid-flow-col grid-rows-7 gap-1.5">
                        {(data?.heatmap || []).map((d) => (
                            <div key={d.date} title={`${d.date}: ${d.words} ${t("reader.words")}`} className="h-4 w-4 rounded-sm" style={{ background: heatColor(d.words, maxHeat) }} />
                        ))}
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-t4">
                        <span className="mono-label">{t("analytics.less")}</span>
                        {[0, 0.25, 0.5, 0.75, 1].map((tv, i) => (
                            <div key={i} className="h-3 w-3 rounded-sm" style={{ background: heatColor(tv * maxHeat, maxHeat) }} />
                        ))}
                        <span className="mono-label">{t("analytics.more")}</span>
                    </div>
                </div>

                <div className="rounded-2xl border border-border bg-surface-1 p-5" data-testid="analytics-badges">
                    <div className="mb-4 flex items-center gap-2">
                        <Trophy className="h-4 w-4 text-[hsl(var(--warning))]" />
                        <h3 className="font-sans-display text-sm font-semibold text-t1">{t("analytics.achievements")}</h3>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {BADGES.map((b) => {
                            const earned = b.test(s);
                            return (
                                <div key={b.key} className={`flex flex-col items-center gap-1.5 rounded-xl border p-3 text-center ${earned ? "border-[hsl(var(--primary))]/40 bg-surface-2" : "border-border bg-surface-1 opacity-50"}`}>
                                    <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${earned ? "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]" : "bg-surface-3 text-t4"}`}>
                                        <b.icon className="h-4 w-4" />
                                    </div>
                                    <span className="text-[11px] text-t2">{t(`analytics.badges.${b.key}`)}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
